import { useLanguage } from "@/lib/LanguageContext";
import { Phone, MessageSquare, MessageCircle, CalendarDays, Zap, DollarSign, Search, Globe } from "lucide-react";

const PHONE_LINK = "tel:+17814756126";
const SMS_LINK = "sms:+17814756126";
const WHATSAPP_LINK = "https://wa.me/17814756126?text=Hi%2C%20I%27d%20like%20to%20schedule%20a%20service";

export const Hero = () => {
  const { t } = useLanguage();

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1764588061773-6200f626c290?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtZWNoYW5pYyUyMHdvcmtpbmclMjBvbiUyMGJtdyUyMHRlc2xhJTIwY2FyJTIwd29ya3Nob3AlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzc0Mjg2ODAwfDA&ixlib=rb-4.1.0&q=85)`,
        }}
      />
      <div className="hero-overlay absolute inset-0" />
      <div className="absolute inset-0 noise-overlay" />

      <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl pt-24 pb-20">
        <div className="max-w-3xl">
          {/* Tagline */}
          <div className="animate-fade-in-up opacity-0" style={{ animationFillMode: 'forwards' }}>
            <span className="inline-block text-teal text-xs tracking-[0.4em] uppercase font-body font-semibold mb-6 border border-teal/20 px-4 py-1.5">
              North Reading, MA
            </span>
          </div>

          {/* Title */}
          <h1 className="animate-fade-in-up opacity-0 delay-100" style={{ animationFillMode: 'forwards' }}>
            <span className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight uppercase text-white block leading-[1.05]">
              {t.hero.title}
            </span>
            <span className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight uppercase block leading-[1.05] mt-1 gold-text">
              {t.hero.titleHighlight}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="font-body text-base md:text-lg text-slate-300 leading-relaxed mt-8 max-w-xl animate-fade-in-up opacity-0 delay-200" style={{ animationFillMode: 'forwards' }}>
            {t.hero.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3 mt-10 animate-fade-in-up opacity-0 delay-300" style={{ animationFillMode: 'forwards' }}>
            <a
              href={PHONE_LINK}
              data-testid="hero-call-btn"
              className="flex items-center gap-2 bg-gold text-black font-heading text-sm tracking-wider uppercase px-7 py-3.5 hover:bg-gold-hover transition-all duration-300 font-bold hover:shadow-[0_0_25px_rgba(212,175,55,0.4)]"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              {t.hero.callNow}
            </a>
            <a
              href={SMS_LINK}
              data-testid="hero-text-btn"
              className="flex items-center gap-2 border border-teal/40 text-teal hover:bg-teal/10 font-heading text-sm tracking-wider uppercase px-7 py-3.5 transition-all duration-300 font-bold"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
              {t.hero.textUs}
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="flex items-center gap-2 border border-green-500/40 text-green-400 hover:bg-green-500/10 font-heading text-sm tracking-wider uppercase px-7 py-3.5 transition-all duration-300 font-bold"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              {t.hero.whatsapp}
            </a>
            <button
              onClick={scrollToContact}
              data-testid="hero-schedule-btn"
              className="flex items-center gap-2 border border-white/20 text-white hover:bg-white/5 font-heading text-sm tracking-wider uppercase px-7 py-3.5 transition-all duration-300 font-bold"
            >
              <CalendarDays className="w-4 h-4" strokeWidth={1.5} />
              {t.hero.schedule}
            </button>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-20 animate-fade-in-up opacity-0 delay-400" style={{ animationFillMode: 'forwards' }}>
          {[
            { icon: Zap, title: t.trust.fast, desc: t.trust.fastDesc },
            { icon: DollarSign, title: t.trust.fair, desc: t.trust.fairDesc },
            { icon: Search, title: t.trust.precise, desc: t.trust.preciseDesc },
            { icon: Globe, title: t.trust.multilingual, desc: t.trust.multilingualDesc },
          ].map((item, i) => (
            <div
              key={i}
              data-testid={`trust-indicator-${i}`}
              className="glass p-5 border border-white/5 hover:border-teal/20 transition-all duration-500 group"
            >
              <item.icon className="w-6 h-6 text-gold mb-3 group-hover:text-teal transition-colors duration-300" strokeWidth={1.5} />
              <h3 className="font-heading text-sm tracking-wider uppercase text-white mb-1">{item.title}</h3>
              <p className="font-body text-xs text-slate-400 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
